import React, { useState, useEffect, useMemo } from 'react';
import MedicalViewsGrid from '../MedicalViewsGrid/medical-views-grid';
import ViewDetails from '../ViewDetails/view-details';
import GeneralViewerDicomMetadataPanel, {
  type DicomMetadataPanelOpenPayload,
} from '../GeneralViewer/GeneralViewerDicomMetadataPanel';
import {
  useGetReportViews,
  useGetGeneralViewerInstances,
  type GeneralViewerInstance,
} from '../../../helpers/api/view_frame/view-frames';

interface SeriesInfo {
  serie_instance_uid: string;
  type: string;
}

interface AiAcceleratorProps {
  studyUID: string;
  selectedFrameViewIds: Record<string, string>;
  onFrameViewUpdated: (viewName: string, frameViewId: string) => void;
  availableSeries: SeriesInfo[];
  /** Called when the user clicks "Assign Image" on a view card — navigate to General Viewer. */
  onAssignInGeneralViewer?: (view: any) => void;
  /**
   * When set, the component auto-selects this view (by view_code) once reportViews loads.
   * Used to navigate directly to View Details after "Confirm & Save" in the General Viewer.
   */
  pendingSelectViewCode?: string | null;
  /** Called after the auto-select has been applied so the parent can clear pendingSelectViewCode. */
  onPendingSelectHandled?: () => void;
}

export default function AiAccelerator({
  studyUID,
  selectedFrameViewIds,
  onFrameViewUpdated,
  availableSeries,
  onAssignInGeneralViewer,
  pendingSelectViewCode,
  onPendingSelectHandled,
}: AiAcceleratorProps) {
  const [selectedView, setSelectedView] = useState(null);
  const [filteredViews, setFilteredViews] = useState([]);
  const [currentViewIndex, setCurrentViewIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState(null);
  const [initialFrameId, setInitialFrameId] = useState<string | undefined>(undefined);

  const [selectedSerieType, setSelectedSerieType] = useState<string[]>(['All']);
  const [dicomMetaPanel, setDicomMetaPanel] = useState<DicomMetadataPanelOpenPayload | null>(
    null
  );

  const {
    data: reportViews,
    isLoading: isLoadingReportViews,
    isError: isErrorReportViews,
    refetch: refetchReportViews, 
  } = useGetReportViews(studyUID, selectedSerieType);

  const seriesIdsForInstances =
    selectedSerieType.length === 1 && selectedSerieType[0] === 'All'
      ? undefined
      : selectedSerieType;
  const { data: generalViewerInstancesData } = useGetGeneralViewerInstances(
    studyUID,
    seriesIdsForInstances
  );
  const generalViewerInstances: GeneralViewerInstance[] = useMemo(() => {
    const raw =
      (generalViewerInstancesData as { data?: { data?: unknown } } | undefined)?.data?.data ??
      (generalViewerInstancesData as { data?: unknown } | undefined)?.data ??
      [];
    return Array.isArray(raw) ? raw : [];
  }, [generalViewerInstancesData]);

  // Auto-select the view that was just assigned in the General Viewer.
  // Fires once reportViews loads (or re-loads after refetch) when pendingSelectViewCode is set.
  // Searches ALL views (not just existing) so it works even with briefly stale cache;
  // the cache is invalidated before navigation so a fresh refetch runs in the background.
  useEffect(() => {
    if (!pendingSelectViewCode || !reportViews) return;
    const allViews: any[] = (reportViews as any)?.data?.views ?? [];
    if (!Array.isArray(allViews)) return;
    const target = allViews.find((v: any) => v.view_code === pendingSelectViewCode);
    if (!target) return;
    const existOnly = allViews.filter((v: any) => v.exists);
    const idx = Math.max(0, existOnly.findIndex((v: any) => v.view_code === pendingSelectViewCode));
    setIsTransitioning(true);
    setSelectedView(target);
    setFilteredViews(existOnly);
    setCurrentViewIndex(idx);
    setAppliedFilters(null);
    setInitialFrameId(undefined);
    onPendingSelectHandled?.();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingSelectViewCode, reportViews]);

  const handleViewSelect = (view, views, index, filters, frameId?: string) => {
    setIsTransitioning(true);
    setSelectedView(view);
    setFilteredViews(views);
    setCurrentViewIndex(index);
    setAppliedFilters(filters);
    setInitialFrameId(frameId);
  };

  const handleBackToGrid = () => {
    setSelectedView(null);
    setInitialFrameId(undefined);
  };

  const handleNextView = () => {
    if (currentViewIndex < filteredViews.length - 1) {
      setIsTransitioning(true);
      const nextIndex = currentViewIndex + 1;
      setCurrentViewIndex(nextIndex);
      setSelectedView(filteredViews[nextIndex]);
      setInitialFrameId(undefined);
    }
  };

  const handlePreviousView = () => {
    if (currentViewIndex > 0) {
      setIsTransitioning(true);
      const prevIndex = currentViewIndex - 1;
      setCurrentViewIndex(prevIndex);
      setSelectedView(filteredViews[prevIndex]);
      setInitialFrameId(undefined);
    }
  };

  const handleTransitionComplete = () => {
    setIsTransitioning(false);
  };

  const handleSerieFilterChange = (serieId: string) => {
    setSelectedSerieType((prev) => {
      if (serieId === 'All') {
        return ['All'];
      }
      let newSelected = prev.filter((id) => id !== 'All');
      const index = newSelected.indexOf(serieId);
      if (index > -1) {
        newSelected.splice(index, 1);
        if (newSelected.length === 0) {
          newSelected = ['All'];
        }
      } else {
        newSelected.push(serieId);
      }
      return newSelected;
    });
  };

  const handleClearAllSeries = () => {
    setSelectedSerieType(['All']);
  };

  const LoadingState = () => (
    <div className="h-full w-full flex items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
    </div>
  );

  const ErrorState = () => (
    <div className="h-full w-full flex items-center justify-center p-4 text-center">
      <p className="text-red-500">
        Error loading AI analysis data. Please try again.
      </p>
    </div>
  );

  const EmptyState = () => (
    <div className="h-full w-full flex items-center justify-center p-4 text-center">
      <p className="text-gray-500">
        No AI analysis data is available for this study.
      </p>
    </div>
  );

  return (
    <div className="h-full flex flex-col min-h-0">
      <GeneralViewerDicomMetadataPanel
        open={dicomMetaPanel !== null}
        onClose={() => setDicomMetaPanel(null)}
        studyUID={dicomMetaPanel?.studyUID ?? studyUID}
        primarySeriesUid={dicomMetaPanel?.primarySeriesUid ?? null}
        selectedInstance={dicomMetaPanel?.selectedInstance ?? null}
        initialTab={dicomMetaPanel?.initialTab ?? 'study'}
      />
      <div className="flex-1 min-h-0">
        {isLoadingReportViews ? (
          <LoadingState />
        ) : selectedView ? (
          <ViewDetails
            onViewDataChange={refetchReportViews}
            view={selectedView}
            onBack={handleBackToGrid}
            onNext={handleNextView}
            onPrevious={handlePreviousView}
            currentIndex={currentViewIndex}
            totalViews={filteredViews.length}
            hasNext={currentViewIndex < filteredViews.length - 1}
            hasPrevious={currentViewIndex > 0}
            studyUID={studyUID}
            isTransitioning={isTransitioning}
            onTransitionComplete={handleTransitionComplete}
            selectedFrameViewIds={selectedFrameViewIds}
            filteredViews={filteredViews}
            onFrameViewUpdated={onFrameViewUpdated}
            appliedFilters={appliedFilters}
            initialFrameId={initialFrameId}
            onOpenDicomMetadata={setDicomMetaPanel}
            studyDicomInstancesForExport={generalViewerInstances}
          />
        ) : (
          <MedicalViewsGrid
            reportViews={reportViews}
            onViewSelect={handleViewSelect}
            availableSeries={availableSeries}
            selectedSerieType={selectedSerieType}
            onSerieFilterChange={handleSerieFilterChange}
            onClearAllSeries={handleClearAllSeries}
            studyUID={studyUID}
            onViewDataChange={refetchReportViews}
            onAssignInGeneralViewer={onAssignInGeneralViewer}
            dicomInstances={generalViewerInstances}
            onOpenDicomMetadata={setDicomMetaPanel}
          />
        )}
      </div>
    </div>
  );
}
